import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addAllergen,
  addCuisinePreference,
  addDietaryProfile,
  removeAllergen,
  removeCuisinePreference,
  removeDietaryProfile,
} from '@/api/endpoints/taste-profile';
import {
  TASTE_PROFILE_QUERY_KEYS,
  tasteProfileQueryOptions,
} from '@/api/queries/taste-profile';
import type { CuisineValence, TasteProfile, TasteRef } from '@/types/taste-profile';

const EMPTY: TasteProfile = { cuisines: [], allergens: [], dietaryProfiles: [] };

/**
 * Reads the current user's Taste Profile and exposes optimistic toggle actions
 * for each facet. Every action updates the cached profile immediately, fires the
 * matching granular endpoint, and invalidates on settle so the server truth wins
 * (which also serves as rollback if the request failed).
 */
export function useTasteProfile() {
  const queryClient = useQueryClient();
  const query = useQuery(tasteProfileQueryOptions());
  const key = TASTE_PROFILE_QUERY_KEYS.current();

  const patch = useCallback(
    (updater: (prev: TasteProfile) => TasteProfile) => {
      queryClient.setQueryData<TasteProfile>(key, (prev) => updater(prev ?? EMPTY));
    },
    [queryClient, key],
  );

  const mutation = useMutation({
    mutationFn: (action: () => Promise<void>) => action(),
    onSettled: () => {
      // Only the final in-flight mutation refetches, so rapid multi-toggles do not
      // transiently drop a still-pending item's optimistic state. The refetch still
      // serves as rollback if a request failed.
      if (queryClient.isMutating() <= 1) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });

  const current = () => queryClient.getQueryData<TasteProfile>(key) ?? EMPTY;

  const toggleCuisine = useCallback(
    (cuisine: TasteRef, valence: CuisineValence) => {
      const existing = current().cuisines.find((c) => c.id === cuisine.id);
      if (existing && existing.valence === valence) {
        patch((prev) => ({ ...prev, cuisines: prev.cuisines.filter((c) => c.id !== cuisine.id) }));
        mutation.mutate(() => removeCuisinePreference(cuisine.id));
        return;
      }
      patch((prev) => ({
        ...prev,
        cuisines: [
          ...prev.cuisines.filter((c) => c.id !== cuisine.id),
          { id: cuisine.id, slug: cuisine.slug, name: cuisine.name, valence },
        ],
      }));
      mutation.mutate(() => addCuisinePreference(cuisine.id, valence));
    },
    [patch, mutation],
  );

  const removeCuisine = useCallback(
    (cuisineId: string) => {
      patch((prev) => ({ ...prev, cuisines: prev.cuisines.filter((c) => c.id !== cuisineId) }));
      mutation.mutate(() => removeCuisinePreference(cuisineId));
    },
    [patch, mutation],
  );

  const toggleAllergen = useCallback(
    (allergen: TasteRef) => {
      const present = current().allergens.some((a) => a.id === allergen.id);
      if (present) {
        patch((prev) => ({ ...prev, allergens: prev.allergens.filter((a) => a.id !== allergen.id) }));
        mutation.mutate(() => removeAllergen(allergen.id));
      } else {
        patch((prev) => ({ ...prev, allergens: [...prev.allergens, allergen] }));
        mutation.mutate(() => addAllergen(allergen.id));
      }
    },
    [patch, mutation],
  );

  const toggleDietary = useCallback(
    (profile: TasteRef) => {
      const present = current().dietaryProfiles.some((d) => d.id === profile.id);
      if (present) {
        patch((prev) => ({ ...prev, dietaryProfiles: prev.dietaryProfiles.filter((d) => d.id !== profile.id) }));
        mutation.mutate(() => removeDietaryProfile(profile.id));
      } else {
        patch((prev) => ({ ...prev, dietaryProfiles: [...prev.dietaryProfiles, profile] }));
        mutation.mutate(() => addDietaryProfile(profile.id));
      }
    },
    [patch, mutation],
  );

  return {
    data: query.data ?? EMPTY,
    isLoading: query.isLoading,
    isError: query.isError,
    toggleCuisine,
    removeCuisine,
    toggleAllergen,
    toggleDietary,
  };
}
